// src/store/slices/uiSlice.js
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  sidebarOpen: false,
  darkMode: false,
  notifications: [],
  snackbar: {
    open: false,
    message: '',
    severity: 'info',
  },
  loading: {
    global: false,
    actions: {},
  },
  dialog: {
    open: false,
    title: '',
    content: '',
    actions: [],
  },
  theme: {
    mode: 'light',
    primary: '#d32f2f',
    secondary: '#1976d2',
  },
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action) => {
      state.sidebarOpen = action.payload;
    },
    toggleDarkMode: (state) => {
      state.darkMode = !state.darkMode;
    },
    setDarkMode: (state, action) => {
      state.darkMode = action.payload;
    },
    addNotification: (state, action) => {
      state.notifications.push({
        id: Date.now(),
        ...action.payload,
        read: false,
        timestamp: new Date().toISOString(),
      });
    },
    markNotificationRead: (state, action) => {
      const notification = state.notifications.find(
        (n) => n.id === action.payload
      );
      if (notification) {
        notification.read = true;
      }
    },
    markAllNotificationsRead: (state) => {
      state.notifications.forEach((n) => {
        n.read = true;
      });
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    showSnackbar: (state, action) => {
      state.snackbar = {
        open: true,
        message: action.payload.message,
        severity: action.payload.severity || 'info',
      };
    },
    hideSnackbar: (state) => {
      state.snackbar.open = false;
    },
    setLoading: (state, action) => {
      const { key, value } = action.payload;
      if (key === 'global') {
        state.loading.global = value;
      } else {
        state.loading.actions[key] = value;
      }
    },
    openDialog: (state, action) => {
      state.dialog = {
        open: true,
        ...action.payload,
      };
    },
    closeDialog: (state) => {
      state.dialog = {
        open: false,
        title: '',
        content: '',
        actions: [],
      };
    },
    setTheme: (state, action) => {
      state.theme = {
        ...state.theme,
        ...action.payload,
      };
    },
  },
});

// Actions
export const {
  toggleSidebar,
  setSidebarOpen,
  toggleDarkMode,
  setDarkMode,
  addNotification,
  markNotificationRead,
  markAllNotificationsRead,
  clearNotifications,
  showSnackbar,
  hideSnackbar,
  setLoading,
  openDialog,
  closeDialog,
  setTheme,
} = uiSlice.actions;

// Selectors
export const selectSidebarOpen = (state) => state.ui.sidebarOpen;
export const selectDarkMode = (state) => state.ui.darkMode;
export const selectNotifications = (state) => state.ui.notifications;
export const selectUnreadNotifications = (state) =>
  state.ui.notifications.filter((n) => !n.read);
export const selectSnackbar = (state) => state.ui.snackbar;
export const selectLoading = (state) => state.ui.loading;
export const selectDialog = (state) => state.ui.dialog;
export const selectTheme = (state) => state.ui.theme;

export default uiSlice.reducer;